// SPDX-License-Identifier: AGPL-3.0-or-later

import {msg} from '@lingui/core/macro';

export const SCREEN_SHARE_DESCRIPTOR = msg({
	message: 'Screen share',
	comment:
		'Video settings label for the text-focused screen share quality preset. Also a settings search synonym, used to match this term when the user types it in the settings search bar.',
});

export const NICKNAME_DESCRIPTOR = msg({
	message: 'Nickname',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const NEW_EMAIL_FORM_DESCRIPTOR = msg({
	message: 'New email form',
	comment: 'Short label in the email change modal. Keep it concise.',
});

export const NEW_EMAIL_DESCRIPTOR = msg({
	message: 'New email',
	comment: 'Short label in the email change modal. Keep it concise.',
});

export const INVALID_OR_EXPIRED_CODE_DESCRIPTOR = msg({
	message: 'Invalid or expired code',
	comment: 'Error message in the password change modal. Keep the tone plain and specific.',
});

export const UNABLE_TO_RESEND_CODE_RIGHT_NOW_DESCRIPTOR = msg({
	message: 'Unable to resend code right now',
	comment: 'Error message in the password change modal. Keep the tone plain and specific.',
});

export const EMAIL_CHANGED_DESCRIPTOR = msg({
	message: 'Email changed',
	comment: 'Short label in the email change modal. Keep it concise.',
});

export const WHAT_S_HAPPENING_DESCRIPTOR = msg({
	message: "What's happening?",
	comment: 'Placeholder question in the custom status text field.',
});

export const CHANGE_EMOJI_DESCRIPTOR = msg({
	message: 'Change emoji',
	comment: 'Short label in the custom status bottom sheet. Keep it concise.',
});

export const CHOOSE_AN_EMOJI_DESCRIPTOR = msg({
	message: 'Choose an emoji',
	comment: 'Button or menu action label in the custom status bottom sheet. Keep it concise.',
});

export const CLEAR_CUSTOM_STATUS_DESCRIPTOR = msg({
	message: 'Clear custom status',
	comment: 'Button or menu action label in the custom status bottom sheet. Keep it concise.',
});

export const CHANGE_YOUR_USERNAME_DESCRIPTOR = msg({
	message: 'Change your username',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});

export const TAG_DESCRIPTOR = msg({
	message: 'Tag',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const MUTUAL_FRIENDS_DESCRIPTOR = msg({
	message: 'Mutual friends',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const EDIT_NOTE_DESCRIPTOR = msg({
	message: 'Edit note',
	comment: 'Button or menu action label in the user settings profile card actions. Keep it concise.',
});

export const PRIVACY_SETUP_STATE_FRIENDS_DESCRIPTOR = msg({
	message: 'Friends only',
	comment:
		'Shown next to a community in the privacy setup modal. Only friends from it can send the user direct messages.',
});

export const VIEW_COMMUNITY_PROFILE_DESCRIPTOR = msg({
	message: 'View community profile',
	comment: 'Button or menu action label in the user profile actions sheet. Keep it concise.',
});

export const VIEW_GLOBAL_PROFILE_DESCRIPTOR = msg({
	message: 'View global profile',
	comment: 'Button or menu action label in the user profile actions sheet. Keep it concise.',
});

export const UNBLOCK_USER_DESCRIPTOR = msg({
	message: 'Unblock user',
	comment:
		'Button or menu action label in the user profile mobile sheet. Keep it concise. Keep the tone plain and specific.',
});

export const SEND_FRIEND_REQUEST_DESCRIPTOR = msg({
	message: 'Send friend request',
	comment: 'Button or menu action label in the user profile mobile sheet. Keep it concise.',
});

export const USER_PROFILE_DESCRIPTOR = msg({
	message: 'User profile',
	comment: 'Short label in the user profile modal. Keep it concise.',
});

export const YOU_CAN_T_MESSAGE_YOURSELF_DESCRIPTOR = msg({
	message: "You can't message yourself",
	comment: 'Error message in the user settings profile preview.',
});

export const MORE_ACTIONS_DESCRIPTOR = msg({
	message: 'More actions',
	comment: 'Short label in the user profile modal. Keep it concise.',
});

export const ANNOUNCE_NEW_MESSAGES_DESCRIPTOR = msg({
	message: 'Announce new messages',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const DOC_I_M_FROM_THE_FUTURE_I_CAME_DESCRIPTOR = msg({
	message:
		"Doc, I'm from the future. I came here in a time machine that you invented. Now, I need your help to get back to the year 1985.",
	comment: 'Label in the accessibility tab.',
});

export const VISUAL_DESCRIPTOR = msg({
	message: 'Visual',
	comment: 'Short label in the accessibility tab. Keep it concise.',
});

export const HIGH_DYNAMIC_RANGE_DESCRIPTOR = msg({
	message: 'High dynamic range',
	comment: 'Settings section label. Also used as a search synonym in the settings search bar.',
});

export const INTERFACE_DESCRIPTOR = msg({
	message: 'Interface',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const STREAMER_MODE_DESCRIPTOR = msg({
	message: 'Streaming privacy',
	comment: 'Settings section label. Also used as a search synonym in the settings search bar.',
});

export const CLEAR_SELECTION_DESCRIPTOR = msg({
	message: 'Clear selection',
	comment: 'Button or menu action label in the authorized apps tab. Keep it concise.',
});

export const DISPLAY_2_DESCRIPTOR = msg({
	message: 'Display',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const INPUT_DESCRIPTOR = msg({
	message: 'Input',
	context: 'chat-settings-section',
	comment: 'Chat settings section for message composer/input behavior.',
});

export const LAUNCH_AT_LOGIN_DESCRIPTOR = msg({
	message: 'Launch {productName} at login',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const RESTART_NOW_DESCRIPTOR = msg({
	message: 'Restart now',
	comment: 'Short label in the desktop settings tab. Keep it concise.',
});

export const RESTART_DESCRIPTOR = msg({
	message: 'Restart {productName}?',
	comment: 'Confirmation prompt in the desktop settings tab. Preserve {productName}; it is inserted by code.',
});

export const LATER_DESCRIPTOR = msg({
	message: 'Later',
	comment: 'Short label in the desktop settings tab. Keep it concise.',
});

export const REVOKE_DEVICE_DESCRIPTOR = msg({
	message: 'Revoke device',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const UNKNOWN_DEVICE_DESCRIPTOR = msg({
	message: 'Unknown device',
	comment: 'Fallback device or operating-system label in the devices tab when session client data is unavailable.',
});

export const URL_DESCRIPTOR = msg({
	message: 'URL',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const UNFURL_DESCRIPTOR = msg({
	message: 'Unfurl',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const INTERFACE_LANGUAGE_DESCRIPTOR = msg({
	message: 'Interface language',
	comment: 'Settings section title in the language tab. Keep it concise.',
});

export const PERSONAL_DICTIONARY_DESCRIPTOR = msg({
	message: 'Personal dictionary',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const THIS_CONNECTION_HAS_BEEN_VERIFIED_DESCRIPTOR = msg({
	message: 'This connection has been verified.',
	comment: 'Description text in the user profile shared popout.',
});

export const THIS_CONNECTION_HAS_NOT_BEEN_VERIFIED_DESCRIPTOR = msg({
	message: 'This connection has not been verified.',
	comment: 'Description text in the user profile shared popout.',
});

export const EDIT_DESCRIPTOR = msg({
	message: 'Edit',
	comment: 'Button or menu action label in the linked accounts tab. Keep it concise.',
});

export const PROFILE_CUSTOMIZATION_DESCRIPTOR = msg({
	message: 'Profile customization',
	comment: 'Settings section label for editing profile appearance and identity.',
});

export const PROFILE_BADGES_DESCRIPTOR = msg({
	message: 'Badges',
	comment: 'Title of the profile settings section that lists the badges you hold and lets you hide each one.',
});

export const MORE_BADGES_DESCRIPTOR = msg({
	message: '+{count}',
	comment:
		'Short count of badges that are not shown next to a name or on a profile, such as +3. Pressing it opens the full list. Keep it as short as possible.',
});

export const SHOW_ALL_BADGES_DESCRIPTOR = msg({
	message: 'Show all badges',
	comment: 'Tooltip for the +3 style button that opens the full list of badges.',
});

export const SHOW_ALL_BADGES_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {+# more badge, show all} other {+# more badges, show all}}',
	comment:
		'Screen reader label for the +3 style button that opens the full list of badges. It starts with the same +3 text that is shown on the button, so voice control users can say it.',
});

export const COMMUNITY_NICKNAME_DESCRIPTOR = msg({
	message: 'Community nickname',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const DISPLAY_NAME_DESCRIPTOR = msg({
	message: 'Display name',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const MENTION_PREFERENCE_SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Mention preference',
	comment: 'Notifications settings: section heading for the default reply-mention behavior.',
});

export const TEXT_TO_SPEECH_NOTIFICATIONS_DESCRIPTOR = msg({
	message: 'Text-to-speech notifications',
	comment: 'Notifications settings: section heading for text-to-speech notification behavior.',
});

export const CAMERA_QUALITY_DESCRIPTOR = msg({
	message: 'Camera quality',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SCREEN_SHARE_QUALITY_DESCRIPTOR = msg({
	message: 'Screen share quality',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const RELEASE_DELAY_DESCRIPTOR = msg({
	message: 'Release delay',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const MIC_TEST_DESCRIPTOR = msg({
	message: 'Mic test',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const ENTRANCE_SOUND_DESCRIPTOR = msg({
	message: 'Entrance sound',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SHOW_FOCUS_RING_ON_CHAT_TEXTAREA_DESCRIPTOR = msg({
	message: 'Show focus ring on chat text area',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const ESCAPE_KEY_EXITS_KEYBOARD_MODE_DESCRIPTOR = msg({
	message: 'Escape key exits keyboard mode',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SHOW_CONTEXT_MENU_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Show context menu keybinds',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const CONFIRM_BEFORE_STARTING_CALLS_DESCRIPTOR = msg({
	message: 'Confirm before starting calls',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SYNC_REDUCED_MOTION_SETTING_WITH_SYSTEM_DESCRIPTOR = msg({
	message: 'Sync reduced motion setting with system',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const STAY_FULLY_INTERACTIVE_WHEN_UNFOCUSED_DESCRIPTOR = msg({
	message: 'Stay fully interactive when unfocused',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const KEEP_ANIMATIONS_GIF_PLAYBACK_HOVER_EFFECTS_AND_TOOLTIPS_DESCRIPTOR = msg({
	message: 'Keep animations, GIF playback, hover effects, and tooltips running while the window is unfocused.',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});

export const SUMMARY_NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Summary row value on the confirm step when no items in the category were selected.',
});

export const ALWAYS_UNDERLINE_LINKS_DESCRIPTOR = msg({
	message: 'Always underline links',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const HIDE_EMAIL_TOGGLE_DESCRIPTOR = msg({
	message: 'Hide',
	comment: 'Account settings: button that hides the user email address (currently revealed). Verb, sentence case.',
});

export const REVEAL_DESCRIPTOR = msg({
	message: 'Reveal',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const VIDEO_SEEK_THUMBNAILS_DESCRIPTOR = msg({
	message: 'Enable video seek thumbnails',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const USE_AUTOSCROLLING_DESCRIPTOR = msg({
	message: 'Use autoscrolling',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const CONFIRM_BEFORE_JOINING_VOICE_CHANNELS_DESCRIPTOR = msg({
	message: 'Confirm before joining voice channels',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const AUTOMATICALLY_SEND_GIFS_WHEN_SELECTED_DESCRIPTOR = msg({
	message: 'Automatically send GIFs when selected',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SAVE_GIF_FAVORITES_AS_SAVED_MEDIA_DESCRIPTOR = msg({
	message: 'Save GIF favorites as saved media',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const UPLOAD_ATTACHMENTS_BEFORE_SENDING_DESCRIPTOR = msg({
	message: 'Upload attachments before sending',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SEQUENTIAL_FILE_SEND_DESCRIPTOR = msg({
	message: 'Send file messages in order',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SCROLL_TO_BOTTOM_WHEN_SENDING_A_MESSAGE_DESCRIPTOR = msg({
	message: 'Scroll to bottom when sending a message',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const SKIP_MARK_ALL_AS_READ_CONFIRMATION_DESCRIPTOR = msg({
	message: 'Skip "Mark all as read" confirmation',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const HIDE_MUTED_CHANNELS_BY_DEFAULT_DESCRIPTOR = msg({
	message: 'Hide muted channels by default',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const CONFIGURE_DESCRIPTOR = msg({
	message: 'Configure',
	comment: 'Button label that opens a dedicated advanced settings modal for configuring screen-share encoder controls.',
});

export const ENABLE_DEVELOPER_MODE_DESCRIPTOR = msg({
	message: 'Enable developer mode',
	comment: 'Settings search entry description. One-line summary of what the settings search entry controls.',
});

export const DOWNLOAD_SOURCE_MAPS_DESCRIPTOR = msg({
	message: 'Download source maps',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const USE_NATIVE_TITLE_BAR_DESCRIPTOR = msg({
	message: 'Use native title bar',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const DISABLE_AUTOMATIC_UPDATES_DESCRIPTOR = msg({
	message: 'Disable automatic updates',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const LOOK_AND_FEEL_DESCRIPTOR = msg({
	message: 'Appearance',
	comment: 'User settings tab for app appearance and layout options.',
});

export const MESSAGES_AND_MEDIA_DESCRIPTOR = msg({
	message: 'Chat',
	comment: 'User settings tab for chat message, media, and input behavior.',
});

export const NOTIFICATIONS_2_DESCRIPTOR = msg({
	message: 'Notifications',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const DESKTOP_DESCRIPTOR = msg({
	message: 'Desktop',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const DEVELOPER_DESCRIPTOR = msg({
	message: 'Developer',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const ENCODER_PATH_DESCRIPTOR = msg({
	message: 'Encoder path',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const AUTOMATIC_DESCRIPTOR = msg({
	message: 'Automatic',
	comment:
		'Settings search synonym. Used to match this term when the user types it in the settings search bar. Default option of the screen share bitrate select in the video tab.',
});

export const SCREEN_SHARE_BITRATE_DESCRIPTOR = msg({
	message: 'Bitrate',
	comment:
		'Short label in the video tab for the screen share video bitrate select. Keep it concise. Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const ALLOW_AV1_SCREEN_SHARE_DESCRIPTOR = msg({
	message: 'Allow AV1 for screen sharing',
	comment: 'Settings search entry label for the AV1 screen-share opt-in. AV1 is a codec name and should stay literal.',
});

export const ALLOW_HEVC_SCREEN_SHARE_DESCRIPTOR = msg({
	message: 'Allow H.265 (HEVC) for screen sharing',
	comment:
		'Settings search entry label for the H.265/HEVC screen-share opt-in. H.265 and HEVC are codec names and should stay literal.',
});

export const SHOW_NEW_DEVICE_ALERTS_DESCRIPTOR = msg({
	message: 'Show new device alerts',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const COMFY_DESCRIPTOR = msg({
	message: 'Comfy',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const DENSE_DESCRIPTOR = msg({
	message: 'Dense',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SPACE_BETWEEN_MESSAGE_GROUPS_DESCRIPTOR = msg({
	message: 'Space between message groups',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SYSTEM_THEME_DESCRIPTOR = msg({
	message: 'System theme',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const CREATE_APPLICATION_DESCRIPTOR = msg({
	message: 'Create application',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const APPLICATION_NAME_DESCRIPTOR = msg({
	message: 'Application name',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const MY_APPLICATION_DESCRIPTOR = msg({
	message: 'My application',
	comment: 'Short label in the application create modal. Keep it concise.',
});

export const REGENERATE_DESCRIPTOR = msg({
	message: 'Regenerate',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const APPLICATION_INFORMATION_DESCRIPTOR = msg({
	message: 'Application information',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const PUBLIC_BOT_DESCRIPTOR = msg({
	message: 'Public bot',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const BOT_PROFILE_DESCRIPTOR = msg({
	message: 'Bot profile',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const BOT_AVATAR_DESCRIPTOR = msg({
	message: 'Bot avatar',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const BOT_USERNAME_DESCRIPTOR = msg({
	message: 'Bot username',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const FRIENDLY_BOT_DESCRIPTOR = msg({
	message: 'Friendly bot',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SECRETS_TOKENS_DESCRIPTOR = msg({
	message: 'Secrets & tokens',
	comment: 'Short label in the secrets section. Keep it concise. Keep the tone plain and specific.',
});

export const CLIENT_SECRET_DESCRIPTOR = msg({
	message: 'Client secret',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const BOT_TOKEN_DESCRIPTOR = msg({
	message: 'Bot token',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const ADD_SEARCH_ENGINE_DESCRIPTOR = msg({
	message: 'Add search engine',
	comment: 'Button or menu action label in the add custom search engine modal. Keep it concise.',
});

export const NAME_DESCRIPTOR = msg({
	message: 'Name',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const CONVERT_EMOTICONS_TO_EMOJI_DESCRIPTOR = msg({
	message: 'Turn text smileys into emoji',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const KEEP_ATTACHMENTS_WHEN_CLEARING_AN_EDIT_DESCRIPTOR = msg({
	message: 'Keep attachments when clearing an edit',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const TRUST_ALL_DESCRIPTOR = msg({
	message: 'Trust all',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const TRUST_ALL_EXTERNAL_LINKS_DESCRIPTOR = msg({
	message: 'Trust all external links',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const STRIP_TRACKING_PARAMETERS_FROM_URLS_DESCRIPTOR = msg({
	message: 'Strip tracking parameters from URLs',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const MEDIA_DISPLAY_DESCRIPTOR = msg({
	message: 'Media display',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const LINK_PREVIEWS_DESCRIPTOR = msg({
	message: 'Link previews',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const BOOKMARK_DESCRIPTOR = msg({
	message: 'Bookmark',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const OPEN_MENU_ICON_DESCRIPTOR = msg({
	message: 'Open menu icon',
	comment: 'Button or menu action label in the overlays tab. Keep it concise.',
});

export const LINKS_DESCRIPTOR = msg({
	message: 'Links',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const DARK_MODE_DESCRIPTOR = msg({
	message: 'Dark mode',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const PREVIEW_SOUND_DESCRIPTOR = msg({
	message: 'Preview sound',
	comment: 'Short label in the entrance sound section. Keep it concise.',
});

export const INVALID_AUDIO_FILE_DESCRIPTOR = msg({
	message: 'Invalid audio file',
	comment: 'Error message in the use entrance sound.',
});

export const RESET_TO_DEFAULTS_DESCRIPTOR = msg({
	message: 'Reset to defaults',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const SYNC_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Sync keybinds',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const CUSTOM_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Custom keybinds',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const DEFAULT_SHORTCUTS_DESCRIPTOR = msg({
	message: 'Default keybinds',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const ACCENT_COLOR_DESCRIPTOR = msg({
	message: 'Accent color',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const USE_GLOBAL_PROFILE_DESCRIPTOR = msg({
	message: 'Use global profile',
	comment: 'Short label in the banner uploader. Keep it concise. Keep the tone plain and specific.',
});

export const USE_CUSTOM_IMAGE_DESCRIPTOR = msg({
	message: 'Use custom image',
	comment: 'Short label in the banner uploader. Keep it concise. Keep the tone plain and specific.',
});

export const DON_T_SHOW_DESCRIPTOR = msg({
	message: "Don't show",
	comment: 'Short label in the banner uploader. Keep it concise. Keep the tone plain and specific.',
});

export const ABOUT_ME_DESCRIPTOR = msg({
	message: 'About me',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const GLOBAL_PROFILE_DESCRIPTOR = msg({
	message: 'Global profile',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const PROFILE_TYPE_DESCRIPTOR = msg({
	message: 'Profile type',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});

export const ENABLE_UNREAD_MESSAGE_BADGE_DESCRIPTOR = msg({
	message: 'Enable unread message badge',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const PER_SOUND_VOLUME_DESCRIPTOR = msg({
	message: 'Per-sound volume',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const PUSH_NOTIFICATION_INACTIVE_TIMEOUT_DESCRIPTOR = msg({
	message: 'Push notification inactive timeout',
	comment: 'Settings search entry label. Names the settings search entry in the settings UI.',
});

export const PROFILE_PRIVACY_LABEL_DESCRIPTOR = msg({
	message: 'Profile privacy',
	comment: 'Privacy > Profile privacy: aria-label for the radio group controlling who sees the user profile.',
});

export const ACTIVITY_SHARING_DESCRIPTOR = msg({
	message: 'Activity sharing',
	comment: 'Privacy settings section for sharing voice activity with friends in active now.',
});

export const SENSITIVE_CONTENT_2_DESCRIPTOR = msg({
	message: 'Sensitive content',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const DATE_MODE_LABEL_DESCRIPTOR = msg({
	message: 'Time range',
	comment: 'Form label for the dropdown that picks between all-time and a custom date range.',
});

export const KIND_GROUP_DMS_LABEL_DESCRIPTOR = msg({
	message: 'Group DMs',
	comment: 'Switch label for including group DMs (3+ participants) in a data export / deletion scope.',
});

export const SUMMARY_ALL_TIME_DESCRIPTOR = msg({
	message: 'All time',
	comment: 'Summary row value on the confirm step indicating no date filter is applied.',
});

export const DELETE_MESSAGES_DESCRIPTOR = msg({
	message: 'Delete messages',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const MUTUAL_GUILDS_DESCRIPTOR = msg({
	message: 'Mutual communities',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const STREAM_PREVIEW_DESCRIPTOR = msg({
	message: 'Stream preview',
	comment: 'Settings search synonym. Used to match this term when the user types it in the settings search bar.',
});

export const GIFTS_DESCRIPTOR = msg({
	message: 'Gifts',
	comment: 'Settings search entry label. Also used as a search synonym in the settings search bar.',
});
